import React, { createContext, useState, useEffect, useMemo, Dispatch, SetStateAction, useContext } from 'react';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { ThemeMode, getTheme } from '../theme';

export enum FontSize {
    Small = 'small',
    Big = 'big',
  }
  

interface IThemeContext {
    currentMode: ThemeMode;
    currentFontSize: FontSize;
    setPrimaryColor: Dispatch<SetStateAction<string>>;
    toggleTheme: () => void;
    toggleFontSize: () => void;
}

export const ThemeContext = createContext<IThemeContext>({
    currentMode: ThemeMode.Light,
    currentFontSize: FontSize.Small,
    setPrimaryColor: () => { },
    toggleTheme: () => { },
    toggleFontSize: () => { },
});

export const CustomThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const loadThemeSettings = () => {
        const savedSettings = localStorage.getItem('themeSettings');
        if (savedSettings) {
            const settings = JSON.parse(savedSettings);
            return {
                theme: getTheme(settings.mode),
                mode: settings.mode,
                fontSize: settings.fontSize
            };
        }
        return {
            theme: getTheme(ThemeMode.Light),
            mode: ThemeMode.Light,
            fontSize: FontSize.Small
        };
    };

    const initialSettings = loadThemeSettings();
    const [theme, setTheme] = useState(initialSettings.theme);
    const [mode, setMode] = useState<ThemeMode>(initialSettings.mode);
    const [fontSize, setFontSize] = useState<FontSize>(initialSettings.fontSize);
    const [primaryColor, setPrimaryColor] = useState<string>('#1976d2');

    useEffect(() => {
        const newTheme = getTheme(mode, primaryColor);
        setTheme(newTheme);
        localStorage.setItem('themeSettings', JSON.stringify({ mode, fontSize }));
         // Dynamic meta tag and body background updates
         const metaThemeColor = document.querySelector('meta[name="theme-color"]');
         const appleStatusBarStyle = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
         const bodyElement = document.body;

         const themeColor = mode === ThemeMode.Light ? '#FFFFFF' : '#232323';
         if (metaThemeColor) metaThemeColor.setAttribute('content', themeColor);
         if (appleStatusBarStyle) appleStatusBarStyle.setAttribute('content', mode === ThemeMode.Light ? 'white' : 'black-translucent');
         if (bodyElement) bodyElement.style.backgroundColor = themeColor;
    }, [mode, fontSize, primaryColor]);

    const themeContextValue = useMemo(() => ({
        currentMode: mode,
        currentFontSize: fontSize,
        setPrimaryColor,
        toggleTheme: () => {
            setMode(prevMode => prevMode === ThemeMode.Light ? ThemeMode.Dark : ThemeMode.Light);
        },
        toggleFontSize: () => {
            setFontSize(prevSize => prevSize === FontSize.Small ? FontSize.Big : FontSize.Small);
        },
    }), [mode, fontSize]);

    return (
        <ThemeContext.Provider value={themeContextValue}>
            <MUIThemeProvider theme={theme}>
                {children}
            </MUIThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useThemeStore = (): IThemeContext => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
      throw new Error("useTenantsStore must be used within a TenantsProvider");
    }
    return context;
  };
  